/**
 * Baked procedural-tree asset schema + runtime loader (issue #58). This is the
 * ONLY tree module the runtime viewer pulls in for data: it fetches the
 * committed data/config/trees.json (produced offline by scripts/bake_trees.ts,
 * synced into public/data by sync-data.mjs) and assembles plain three.js
 * BufferGeometry from the baked vertex arrays. No skeleton is grown at runtime
 * (issue #17): geometry generation happened at build time.
 */
import { BufferAttribute, BufferGeometry } from 'three';
import type { GeometryArrays } from './treeTypes.ts';

export interface BakedPool {
  species: string;
  speciesIndex: number;
  variantIndex: number;
  atlasColumn: number;
  /** grown skeleton height (m); runtime scales a placed instance by
   * desiredHeight / height. */
  height: number;
  bark: GeometryArrays;
  foliage: GeometryArrays;
}

export interface BakedTrees {
  /** attribution + provenance carried into the shipped asset */
  attribution: string;
  generatedBy: string;
  atlasCols: number;
  atlasRows: number;
  pools: BakedPool[];
}

/** Assembles an indexed BufferGeometry from baked flat arrays. */
export function geometryFromArrays(arrays: GeometryArrays): BufferGeometry {
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(arrays.positions), 3));
  g.setAttribute('normal', new BufferAttribute(new Float32Array(arrays.normals), 3));
  g.setAttribute('uv', new BufferAttribute(new Float32Array(arrays.uvs), 2));
  g.setIndex(arrays.indices);
  g.computeBoundingSphere();
  return g;
}

/** Fetches the baked tree asset. Throws on a missing/failed fetch (hard
 * constraint #7 — the caller reports it as a blocker, never fabricates trees). */
export async function loadTrees(url = '/data/config/trees.json'): Promise<BakedTrees> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch baked trees at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as BakedTrees;
}
